// =============================================================
// main.ts — UI 바인딩. SDK 파사드만 사용(체인 세부는 모름).
// =============================================================

import './style.css';
import {
  DigMolSDK,
  fmtMolInt,
  pvProgress,
  isDener,
  remainingToDener,
  hasAddress,
  TxState,
  SdkError,
  PV_GATE_ADDRESS,
  CHAIN,
  ONE,
  type TxUpdate,
  type WalletRole,
  type DistributorRow,
  type FeedItem,
} from '@sdk/index';
import { parseEther, formatEther, isAddress } from 'ethers';
import { t, initI18n, setLocale, getLocale, onLocaleChange, type Locale } from './i18n';

// sdk 는 부팅 시 DigMolSDK.create() 로 환경에 맞게(injected/Reown) 초기화된다.
//  · 모듈 로드 시점엔 아직 생성하지 않는다(!). boot() 에서 await 후 wire() 를 부른다.
//  · 모든 sdk.* 참조는 이벤트 핸들러/함수 내부라 boot 완료 후에만 실행된다.
let sdk!: DigMolSDK;
let connected = false;

const $ = <T extends HTMLElement = HTMLElement>(id: string) => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
};

// ---------- 지갑 ----------
// 연결됨 UI 반영 (최초 연결 / 새로고침 후 세션 복원 공용)
async function reflectConnected(): Promise<void> {
  connected = true;
  const snap = sdk.wallet.snapshot();
  $('wbtn').classList.add('on');
  $('wlabel').textContent = snap.short ?? t('wallet.connected');
  ($('wcaret') as HTMLElement).style.display = '';
  const act = $('actBtn');
  act.classList.remove('connect');
  act.textContent = t('cta.mine');
  await loadContract();
  await refresh();
  await detectRole();
  void renderDomainBox('vDomainBox'); // 하단 패널 대표 도메인 안내
  void loadDistributorSelectOptions(true); // 연결 전 실패/빈 캐시를 버리고 총판 목록 재조회
  updateCtaState(); // .dex 미조회 → 버튼 비활성화
}

// 외부(지갑 앱)에서 연결 해제된 경우 UI 를 비연결 상태로 되돌림
function reflectDisconnected(): void {
  connected = false;
  $('wmenu').classList.remove('open');
  $('wbtn').classList.remove('on');
  $('wlabel').textContent = t('wallet.connect');
  ($('wcaret') as HTMLElement).style.display = 'none';
  $('actBtn').classList.add('connect');
  $('actBtn').textContent = t('wallet.connect');
  $('vDomainBox').className = 'm-domainbox';
  $('vDomainBox').innerHTML = '';
  mResolvedDist = null;
  vResolvedDist = null;
  updateCtaState(); // 비연결 → 버튼 활성(연결 동작)
}

// 페이지 로드 시 기존 세션 복원
async function restoreSession(): Promise<void> {
  try {
    await sdk.wallet.restore();
  } catch {
    return;
  }
  if (sdk.wallet.getAddress()) await reflectConnected();
}

async function connectWallet(): Promise<void> {
  if (connected) return; // 이미 연결됨
  clearErr();
  stuckNoticeShown = false; // 새 시도 → stuck 안내 리셋

  // ★ 카카오톡/라인 등 메신저 인앱 브라우저 → 외부 브라우저로 탈출.
  //   메신저 웹뷰는 MetaMask 딥링크(외부 앱 스킴)를 막기 때문에,
  //   여기서 먼저 외부 브라우저(크롬/사파리)로 내보낸다.
  //   외부 브라우저로 다시 열리면 그곳에서 아래 딥링크 흐름이 정상 동작한다.
  if (isInAppMessenger()) {
    escapeToExternalBrowser();
    return;
  }

  // 모바일 + 주입 지갑 없음(일반 브라우저) → 곧바로 MetaMask 앱으로 이동.
  //  · 선택 UI 없이 딥링크로 바로 보낸다(클릭 한 번으로 단순하게).
  //  · MetaMask 내장 브라우저에서 열리면 injected 로 연결된다.
  if (isMobileWithoutWallet()) {
    openInMetaMaskApp();
    return;
  }

  // single-flight 는 SDK(injected.connect)가 담당한다.
  //  · 진행 중이면 같은 promise 를 돌려주므로 중복 팝업/충돌이 없다.
  //  · 따라서 클릭마다 그냥 connect() 를 불러도 안전하다.
  try {
    await sdk.wallet.connect();
    if (!connected) await reflectConnected();
    stuckNoticeShown = false; // 연결 성공 → 안내 해제
    // 연결 성공 → 딥링크 실행 기록 정리(다음 시도를 깨끗하게)
    try {
      sessionStorage.removeItem('mm_deeplink_launched');
    } catch {
      /* noop */
    }
  } catch (err) {
    if (err instanceof SdkError && err.code === 'NO_WALLET') {
      showErr((err as SdkError).message || t('wallet.noWalletFallback'));
    } else if (err instanceof SdkError && err.code === 'USER_REJECTED') {
      showErr(t('wallet.connectCancelled'));
    } else {
      showErr(t('wallet.connectFail', { msg: humanTxErr(err) }));
    }
  }
}

async function loadContract(): Promise<void> {
  const meta = $('contractMeta');
  if (!hasAddress(PV_GATE_ADDRESS)) {
    meta.textContent = t('contract.noAddr');
    return;
  }
  const link = `<a href="${CHAIN.explorer}/address/${PV_GATE_ADDRESS}" target="_blank" rel="noopener" style="color:var(--gold);font-family:var(--fm)">${PV_GATE_ADDRESS.slice(0, 10)}…</a>`;
  meta.innerHTML = t('contract.vein', { link });
}

// ---------- 조회/표시 ----------
async function refresh(): Promise<void> {
  if (!hasAddress(PV_GATE_ADDRESS)) {
    $('molBal').textContent = '—';
    return;
  }
  try {
    const info = await sdk.swap.info();
    const bnbUsd = Number(info.bnbUsd8) / 1e8;
    const molUsd = Number(info.molPriceUsd8) / 1e8;
    $('stRate').textContent = '$' + molUsd.toFixed(6);
    $('stStock').textContent = fmtMolInt(info.stock);
    $('rateNote').textContent = t('refresh.rateNote', {
      mol: '$' + molUsd.toFixed(6),
      bnb: '$' + bnbUsd.toLocaleString('en-US', { maximumFractionDigits: 2 }),
      stock: fmtMolInt(info.stock),
    });

    if (info.myMol !== null) {
      const mol = info.myMol;
      const whole = mol / ONE;
      $('molBal').textContent = whole.toLocaleString('en-US');
      $('stMol').textContent = whole.toLocaleString('en-US');

      const pct = pvProgress(mol);
      ($('fill') as HTMLElement).style.width = pct + '%';
      $('pvPct').textContent = pct.toFixed(1) + '%';

      const dener = isDener(mol);
      $('fill').classList.toggle('met', dener);
      const st = $('pvSt');
      st.className = 'st ' + (dener ? 'met' : 'pending');
      st.textContent = dener ? t('status.dener') : t('status.pending');

      const inv = $('inv');
      if (dener) {
        inv.className = 'investiture show done';
        inv.innerHTML = t('inv.done');
      } else {
        inv.className = 'investiture show';
        const r = remainingToDener(mol);
        inv.innerHTML = t('inv.remaining', { amount: r.toLocaleString('en-US') });
      }
    } else {
      $('molBal').textContent = '—';
      $('stMol').textContent = '—';
    }
  } catch (err) {
    showErr(t('refresh.queryFail', { msg: (err as Error).message }));
  }
}

// ---------- quote ----------
let qTimer: number | undefined;
function onAmount(): void {
  clearErr();
  const raw = ($('bnbIn') as HTMLInputElement).value.trim();
  if (!raw || Number.isNaN(Number(raw)) || Number(raw) <= 0) {
    $('molOut').textContent = '—';
    return;
  }
  window.clearTimeout(qTimer);
  qTimer = window.setTimeout(async () => {
    try {
      if (!hasAddress(PV_GATE_ADDRESS)) {
        $('molOut').textContent = '—';
        return;
      }
      const q = await sdk.swap.quote(parseEther(raw), vResolvedDist);
      $('molOut').textContent = q.molOut > 0n ? fmtMolInt(q.molOut) + ' MOL' : '—';
    } catch {
      $('molOut').textContent = '—';
    }
  }, 220);
}

async function setMax(): Promise<void> {
  if (!connected) {
    await connectWallet();
    return;
  }
  try {
    const me = sdk.wallet.getAddress();
    if (!me) return;
    const bal = await sdk.chain.bnbBalance(me);
    const reserve = parseEther('0.002'); // 가스 여유
    const use = bal > reserve ? bal - reserve : 0n;
    ($('bnbIn') as HTMLInputElement).value = formatEther(use);
    onAmount();
  } catch {
    /* noop */
  }
}

// ---------- swap ----------
async function onAct(): Promise<void> {
  if (!connected) {
    await connectWallet();
    return;
  }
  await doSwap();
}

async function doSwap(): Promise<void> {
  clearErr();
  const raw = ($('bnbIn') as HTMLInputElement).value.trim();
  if (!raw || Number(raw) <= 0) {
    showErr(t('swap.needAmount'));
    return;
  }
  if (!hasAddress(PV_GATE_ADDRESS)) {
    showErr(t('swap.noAddr'));
    return;
  }
  // 할인 도메인 필수: .dex 가 유효하게 조회되지 않으면 진행 불가
  if (!vResolvedDist) {
    showErr(t('dist.required'));
    return;
  }
  const btn = $('actBtn') as HTMLButtonElement;
  btn.disabled = true;
  try {
    await sdk.swap.swap(parseEther(raw), vResolvedDist, 0n, (u: TxUpdate) => {
      if (u.state === TxState.Signing) {
        setTx('info', `<span class="sp"></span><div>${t('tx.signing')}</div>`);
      } else if (u.state === TxState.Pending) {
        setTx(
          'info',
          `<span class="sp"></span><div>${t('tx.pendingMining')}<br><a href="${CHAIN.explorer}/tx/${u.hash}" target="_blank" rel="noopener">${u.hash?.slice(0, 18)}…</a></div>`,
        );
      } else if (u.state === TxState.Confirming) {
        setTx('info', `<span class="sp"></span><div>${t('tx.confirming')}</div>`);
      } else if (u.state === TxState.Success) {
        const got = u.molOut != null ? fmtMolInt(u.molOut) + ' MOL' : t('tx.successDefault');
        setTx(
          'ok',
          `<div>${t('tx.successMined', { got })}<br><a href="${CHAIN.explorer}/tx/${u.hash}" target="_blank" rel="noopener">${t('tx.viewTx')}</a><button type="button" id="addMolBtn" style="${ADD_MOL_BTN_STYLE}">${tAddMol('tx.addMol', '지갑에 MOL 추가')}</button></div>`,
        );
        void promptAddMol(false); // 자동 등록 유도
        document.getElementById('addMolBtn')?.addEventListener('click', () => void promptAddMol(true));
      }
    });
    await refresh();
  } catch (err) {
    const code = err instanceof SdkError ? err.code : 'UNKNOWN';
    setTx('err', `<div>${t('swap.failPrefix', { msg: humanErr(code, (err as Error).message) })}</div>`);
  } finally {
    btn.disabled = false;
  }
}

function humanErr(code: string, fallback: string): string {
  const map: Record<string, string> = {
    USER_REJECTED: t('err.userRejected'),
    INSUFFICIENT_BNB: t('err.insufficientBnb'),
    INSUFFICIENT_MOL_STOCK: t('err.insufficientStock'),
    GATE_CLOSED: t('err.gateClosed'),
    ZERO_AMOUNT: t('err.zeroAmount'),
    NO_GATE_ADDRESS: t('err.noGateAddr'),
  };
  return map[code] ?? fallback;
}

// 지갑/체인 raw 에러 → 사람이 읽을 메시지 (admin·총판 트랜잭션용)
function humanTxErr(err: unknown): string {
  const m =
    (err as { shortMessage?: string })?.shortMessage ??
    (err as { message?: string })?.message ??
    String(err);
  if (/user rejected|user denied|ACTION_REJECTED|4001/i.test(m)) return t('err.tx.userCancelled');
  if (/insufficient funds/i.test(m)) return t('err.tx.insufficientGas');
  if (/NOT_OWNER/i.test(m)) return t('err.tx.notOwner');
  if (/NOT_DIST/i.test(m)) return t('err.tx.notDist');
  if (/OWNER_LOCKED/i.test(m)) return t('err.tx.ownerLocked');
  if (/BAD_DISCOUNT/i.test(m)) return t('err.tx.badDiscount');
  if (/BAD_REWARD/i.test(m)) return t('err.tx.badReward');
  if (/network|timeout|could not detect/i.test(m)) return t('err.tx.network');
  // 알 수 없는 경우: raw 전체 대신 짧게만
  return t('err.tx.unknown');
}

// ---------- 모바일 지갑 환경 감지 & MetaMask 딥링크 ----------
// 일반 모바일 브라우저(크롬/사파리)에는 window.ethereum 이 없어 injected 가 안 된다.
// 이 경우 MetaMask 앱 내장 브라우저로 열도록 딥링크로 유도한다(연결/복귀가 매끄러움).
function isMobileUA(): boolean {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}
function hasInjectedProvider(): boolean {
  const eth = (window as unknown as { ethereum?: { providers?: unknown[] } }).ethereum;
  if (!eth) return false;
  if (Array.isArray(eth.providers)) return eth.providers.length > 0;
  return true;
}
/** 모바일인데 주입 지갑이 없으면(=일반 브라우저) true. */
function isMobileWithoutWallet(): boolean {
  return isMobileUA() && !hasInjectedProvider();
}

/** 카카오톡/라인/텔레그램/WhatsApp/네이버/페북/인스타 등 메신저 인앱 브라우저인지 감지. */
function isInAppMessenger(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /kakaotalk|line\/|telegram|whatsapp|wechat|micromessenger|fb_iab|fban|fbav|instagram|naver|whale|band|daumapps|snapchat|twitter/i.test(
    ua,
  );
}

/**
 * 메신저 인앱 브라우저 → 기기 기본 브라우저(크롬/사파리)로 탈출.
 *  · 카카오톡: kakaotalk://web/openExternal?url=… (안드로이드=크롬, iOS=사파리)
 *  · 라인:     현재 URL 에 ?openExternalBrowser=1 (이미 ? 있으면 &)
 *  · 텔레그램/WhatsApp 등: 전용 '외부 열기' 스킴이 없다 →
 *      안드로이드는 intent 스킴으로 크롬 강제, iOS 는 URL 복사 후 수동 안내.
 * 널리 쓰이는 표준 우회 방식.
 */
function escapeToExternalBrowser(): void {
  const ua = navigator.userAgent.toLowerCase();
  const url = window.location.href;

  // 전용 스킴이 있는 메신저 (가장 매끄러움)
  if (/kakaotalk/i.test(ua)) {
    window.location.href = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(url);
    return;
  }
  if (/line\//i.test(ua)) {
    window.location.href = url + (url.includes('?') ? '&' : '?') + 'openExternalBrowser=1';
    return;
  }

  // 그 외 메신저(텔레그램/WhatsApp/인스타/페북 등): 전용 스킴 없음.
  //  · 안드로이드 → intent 로 크롬 강제 실행 시도
  if (/android/i.test(ua)) {
    const noScheme = url.replace(/^https?:\/\//, '');
    window.location.href =
      'intent://' + noScheme + '#Intent;scheme=https;package=com.android.chrome;end';
    return;
  }

  // · iOS → 자동 탈출이 막혀 있다. URL 복사 후 Safari 붙여넣기 안내.
  const done = () =>
    showErr(
      tAddMol(
        'inapp.copyGuide',
        '브라우저에서 열어야 지갑을 연결할 수 있습니다. 주소가 복사되었으니 Safari에 붙여넣어 주세요.',
      ),
    );
  try {
    void navigator.clipboard.writeText(url).then(done, done);
  } catch {
    done();
  }
}

/** 현재 페이지를 MetaMask 앱 내장 브라우저로 여는 딥링크. */
function metamaskDeepLink(): string {
  // metamask.app.link/dapp/<host><path> — 스킴(https://) 제외
  const host = window.location.host; // digmol.com
  const path = window.location.pathname + window.location.search;
  return `https://metamask.app.link/dapp/${host}${path}`;
}
function openInMetaMaskApp(): void {
  // 딥링크는 편도(one-way) 이동이다.
  //  · 크롬에서 MetaMask 앱으로 넘어가면, 그 뒤엔 MetaMask 내장 브라우저에서 계속 써야 한다.
  //  · 사용자가 크롬으로 돌아와 재실행하면, 이미 열린 MetaMask 앱과 충돌해 멈춘다.
  //  · 따라서 '실행했음' 을 sessionStorage 에 기록하고, 짧은 시간 내 재실행을 막는다.
  try {
    sessionStorage.setItem('mm_deeplink_launched', String(Date.now()));
  } catch {
    /* sessionStorage 불가 환경 무시 */
  }
  window.location.href = metamaskDeepLink();
}


// 스왑 후 지갑에 MOL 토큰을 등록하도록 유도. 자동 1회 + 버튼 백업.
//  · i18n 키가 없어도 폴백으로 동작(ko/en/ja/zh 에 tx.addMol 추가 시 번역 우선).
function tAddMol(key: string, fallback: string, params?: Record<string, string>): string {
  const out = t(key, params);
  if (out === key || out.startsWith('tx.')) {
    return params ? fallback.replace(/\{(\w+)\}/g, (_, k) => params[k] ?? `{${k}}`) : fallback;
  }
  return out;
}
let molWatchDone = false;
async function promptAddMol(fromButton: boolean): Promise<void> {
  if (!fromButton && molWatchDone) return; // 자동은 세션당 1회
  try {
    const ok = await sdk.chain.watchMol();
    if (ok) molWatchDone = true;
  } catch (err) {
    if (fromButton) {
      showErr(tAddMol('tx.addMolFail', `MOL 추가 실패: ${humanTxErr(err)}`, { msg: humanTxErr(err) }));
    }
  }
}

const ADD_MOL_BTN_STYLE =
  'margin-left:8px;padding:4px 10px;font-size:11px;border-radius:6px;background:var(--gold,#D4A843);color:#0A0C14;font-weight:700;cursor:pointer;border:none';

// ---------- 작은 DOM 유틸 ----------
function setTx(kind: 'info' | 'ok' | 'err', html: string): void {
  const el = $('txbox');
  el.className = 'txbox show ' + kind;
  el.innerHTML = html;
}
function showErr(msg: string): void {
  const el = $('errbar');
  el.textContent = msg;
  el.classList.add('show');
}
function clearErr(): void {
  $('errbar').classList.remove('show');
}

// 지갑이 응답하지 않을 때(모바일 MetaMask stuck) 새로고침 안내.
//  · errbar 에 안내문 + [새로고침] 버튼을 띄운다(탈출구 제공).
let stuckNoticeShown = false;
function showStuckNotice(): void {
  if (stuckNoticeShown) return;
  stuckNoticeShown = true;
  const el = $('errbar');
  const msg = tAddMol(
    'wallet.stuckNotice',
    '지갑이 응답하지 않습니다. 새로고침하거나 MetaMask 앱을 재시작해 주세요.',
  );
  const btnLabel = tAddMol('wallet.reload', '새로고침');
  el.innerHTML = `${msg} <button type="button" id="reloadBtn" style="${ADD_MOL_BTN_STYLE}">${btnLabel}</button>`;
  el.classList.add('show');
  document.getElementById('reloadBtn')?.addEventListener('click', () => location.reload());
}

// ---------- 스크롤 리빌 + 깊이 레일 ----------
function initScroll(): void {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) if (e.isIntersecting) e.target.classList.add('in');
    },
    { threshold: 0.16 },
  );
  document.querySelectorAll('.reveal').forEach((el) => io.observe(el));

  const segs = [...document.querySelectorAll<HTMLElement>('.rail .seg')];
  const rio = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          const s = Number((e.target as HTMLElement).dataset.s);
          segs.forEach((sg, i) => sg.classList.toggle('on', i <= s));
        }
      }
    },
    { threshold: 0.5 },
  );
  document.querySelectorAll<HTMLElement>('section[data-s]').forEach((s) => rio.observe(s));
}

// =============================================================
//  Dener 서임 모달
// =============================================================

// 연결된 지갑 주소 → 대표 .dex 도메인 역조회
async function resolvePrimaryDomain(address: string): Promise<string | null> {
  try {
    const res = await fetch(`https://dexignation.com/api/v1/addresses/${address}/primary`);
    if (!res.ok) return null;
    const json = (await res.json()) as { data?: { name?: string | null } };
    return json.data?.name ?? null;
  } catch {
    return null;
  }
}

// 구매자 대표 도메인 안내(역조회): 있으면 표시, 없으면 발급 절차 안내.
// boxId 로 모달(mDomainBox)·하단 패널(vDomainBox) 어디든 렌더.
async function renderDomainBox(boxId: string): Promise<void> {
  const box = $(boxId);
  const me = connected ? sdk.wallet.getAddress() : null;
  if (!me) {
    box.className = 'm-domainbox';
    box.innerHTML = '';
    return;
  }
  const short = me.slice(0, 6) + '…' + me.slice(-4);
  box.className = 'm-domainbox show';
  box.innerHTML = `<div class="m-dom-loading">${t('domain.checking', { addr: short })}</div>`;
  const name = await resolvePrimaryDomain(me);
  // 비동기 사이 지갑이 끊겼으면 무시
  if (!connected || sdk.wallet.getAddress() !== me) return;
  if (name) {
    box.className = 'm-domainbox show ok';
    box.innerHTML =
      `<div class="m-dom-wallet">${t('domain.checkedWallet', { addr: short })}</div>` +
      `<div class="m-dom-result ok">${t('domain.have', { name })}</div>`;
  } else {
    box.className = 'm-domainbox show guide';
    box.innerHTML =
      `<div class="m-dom-wallet">${t('domain.checkedWallet', { addr: short })}</div>` +
      `<div class="m-dom-result">${t('domain.noneTitle')}</div>` +
      `<div class="m-dom-title">${t('domain.guideTitle')}</div>` +
      `<ol class="m-dom-steps"><li>${t('domain.step1')}</li><li>${t('domain.step2')}</li><li>${t('domain.step3')}</li></ol>` +
      `<a class="m-dom-link" href="https://dexignation.com" target="_blank" rel="noopener">${t('domain.link')}</a>` +
      `<button class="m-dom-recheck" type="button">${t('domain.recheck')}</button>`;
    box.querySelector('.m-dom-recheck')?.addEventListener('click', () => void renderDomainBox(boxId));
  }
}

// 모달/패널 도메인 박스를 상황에 맞게 갱신
function refreshDomainBoxes(): void {
  if (connected) void renderDomainBox('vDomainBox');
  if (connected && $('modalOverlay').classList.contains('open')) void renderDomainBox('mDomainBox');
}

// "서임받기" 진입점: 미연결이면 지갑 연결(MetaMask 팝업)부터 띄우고,
// 연결에 성공한 경우에만 서임 모달을 연다.
//  · 사용자가 연결을 거부/실패하면 모달을 열지 않는다(빈 모달 방지).
//  · 이미 연결돼 있으면 곧바로 모달을 연다.
async function onBecome(): Promise<void> {
  if (!connected) {
    await connectWallet();
    if (!connected) return; // 연결 취소/실패 → 모달 열지 않음
  }
  openModal();
}

function openModal(): void {
  $('modalOverlay').classList.add('open');
  $('modalOverlay').setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  void renderDomainBox('mDomainBox');
  const btn = $('mActBtn');
  if (connected) {
    btn.classList.remove('connect');
    btn.textContent = t('cta.mine');
    void mRefresh();
  } else {
    btn.classList.add('connect');
    btn.textContent = t('wallet.connect');
  }
  updateCtaState(); // 모달 열릴 때 .dex 미조회면 비활성화
  if (hasAddress(PV_GATE_ADDRESS)) {
    const link = `<a href="${CHAIN.explorer}/address/${PV_GATE_ADDRESS}" target="_blank" rel="noopener" style="color:var(--gold)">${PV_GATE_ADDRESS.slice(0, 10)}…</a>`;
    $('mContractMeta').innerHTML = t('contract.vein', { link });
    void loadPrice();
  } else {
    $('mContractMeta').textContent = t('modal.noAddrMeta');
    $('mPriceNote').textContent = t('modal.priceNoteNoAddr');
  }
  void loadDistributorSelectOptions(true); // 모달을 열 때마다 등록 총판 목록 새로 조회
}

function closeModal(): void {
  $('modalOverlay').classList.remove('open');
  $('modalOverlay').setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

// 현재 슬라이더가 가리키는 MOL 수량(정수)과 그에 필요한 BNB(wei)
let curMolWhole = 20_000_000n;
let curBnbWei = 0n;
let priceLoaded = false;

async function loadPrice(): Promise<void> {
  if (!hasAddress(PV_GATE_ADDRESS)) return;
  try {
    const info = await sdk.swap.info();
    priceLoaded = true;
    const bnbUsd = Number(info.bnbUsd8) / 1e8;
    const molUsd = Number(info.molPriceUsd8) / 1e8;
    $('mPriceNote').textContent = t('modal.priceNote', {
      mol: '$' + molUsd.toFixed(6),
      bnb: '$' + bnbUsd.toLocaleString('en-US', { maximumFractionDigits: 2 }),
    });
    await recalcPay();
  } catch {
    $('mPriceNote').textContent = t('modal.priceFail');
  }
}

// 슬라이더 → MOL 수량 표시 + 필요 BNB 계산 + quote
let payTimer: number | undefined;
function onMolRange(): void {
  const val = ($('mMolRange') as HTMLInputElement).value;
  curMolWhole = BigInt(val);
  $('mMolNum').textContent = curMolWhole.toLocaleString('en-US');
  window.clearTimeout(payTimer);
  payTimer = window.setTimeout(() => void recalcPay(), 120);
}

async function recalcPay(): Promise<void> {
  if (!hasAddress(PV_GATE_ADDRESS) || !priceLoaded) return;
  try {
    const molWei = curMolWhole * ONE;
    // 컨트랙트는 받은 BNB 를 molForBnb 로 내림 환산해 MIN_MOL(2천만) 을 검사한다.
    // bnbForMol→molForBnb 왕복 내림으로 목표보다 살짝 모자라 revert(BELOW_MIN_MOL) 되는
    // 경계 문제를 막기 위해 1 MOL 여유를 더해 지불 BNB 를 계산한다.
    curBnbWei = await sdk.swap.bnbForMol(molWei + ONE);
    $('mBnbPay').textContent = Number(formatEther(curBnbWei)).toLocaleString('en-US', { maximumFractionDigits: 4 });
    await mQuote();
  } catch {
    $('mBnbPay').textContent = '—';
  }
}

async function mRefresh(): Promise<void> {
  if (!hasAddress(PV_GATE_ADDRESS)) return;
  try {
    const info = await sdk.swap.info();
    if (info.myMol !== null) {
      const mol = info.myMol;
      const whole = mol / ONE;
      $('mMolBal').textContent = whole.toLocaleString('en-US');
      const pct = pvProgress(mol);
      ($('mFill') as HTMLElement).style.width = pct + '%';
      $('mPvPct').textContent = pct.toFixed(1) + '%';
      const dener = isDener(mol);
      $('mFill').classList.toggle('met', dener);
      const st = $('mPvSt');
      st.className = 'm-st ' + (dener ? 'met' : 'pending');
      st.textContent = dener ? t('status.dener') : t('status.pending');
      const inv = $('mInv');
      if (dener) {
        inv.className = 'm-investiture show done';
        inv.innerHTML = t('inv.done');
      } else {
        inv.className = 'm-investiture show';
        const r = remainingToDener(mol);
        inv.innerHTML = t('inv.remaining', { amount: r.toLocaleString('en-US') });
      }
    }
  } catch {
    /* noop */
  }
}

// 총판 .dex 이름 입력 → DEXignation 으로 BNB 주소 resolve → 유효성/할인율 표시
// 모달(mDistIn)·하단 패널(vDistIn) 공용. 각자의 resolved 주소를 보관한다.
let mResolvedDist: string | null = null;
let vResolvedDist: string | null = null;
let aResolvedRegName: string | null = null;
let aResolvedRegAddr: string | null = null;
const distTimers: Record<string, number | undefined> = {};

interface DistributorSelectOption {
  address: string;
  label: string;
  active: boolean;
  discountBp: number;
  rewardBp: number;
}

let distributorSelectOptions: DistributorSelectOption[] = [];
let distributorSelectPromise: Promise<void> | null = null;

// DEXignation name resolver: "alice.dex" → BNB 주소
type ResolveResult =
  | { ok: true; address: string }
  | { ok: false; reason: 'not_found' | 'no_bnb' | 'error' };

async function resolveDexName(name: string): Promise<ResolveResult> {
  try {
    const url = `https://dexignation.com/api/v1/names/${encodeURIComponent(name)}?coins=BNB`;
    const res = await fetch(url);
    if (!res.ok) {
      console.warn('[dexignation] HTTP', res.status, name);
      return { ok: false, reason: 'error' };
    }
    const json = (await res.json()) as {
      data?: { exists?: boolean; addresses?: { coinType?: number; symbol?: string; address?: string | null }[] };
    };
    console.info('[dexignation] resolve', name, json);
    const rec = json.data;
    if (!rec?.exists) return { ok: false, reason: 'not_found' };
    const entry = (rec.addresses ?? []).find((a) => a.symbol === 'BNB');
    if (!entry?.address) return { ok: false, reason: 'no_bnb' };
    return { ok: true, address: entry.address };
  } catch (e) {
    console.warn('[dexignation] fetch error', name, e);
    return { ok: false, reason: 'error' };
  }
}

function setDistributorSelectNote(selectId: 'mDistIn' | 'vDistIn', noteId: 'mDistNote' | 'vDistNote'): void {
  const select = $(selectId) as HTMLSelectElement;
  const note = $(noteId);
  const opt = distributorSelectOptions.find((o) => o.address === select.value);
  if (!select.value) {
    note.className = 'm-distnote show req';
    note.textContent = distributorSelectOptions.length > 0 ? t('dist.selectRequired') : t('dist.noDistributors');
    return;
  }
  if (!opt) {
    note.className = 'm-distnote show no';
    note.textContent = t('dist.selectInvalid');
    return;
  }
  if (!opt.active) {
    note.className = 'm-distnote show no';
    note.textContent = t('dist.selectedInactiveDistributor', { name: opt.label });
    return;
  }
  note.className = 'm-distnote show ok';
  const dp = (opt.discountBp / 100).toFixed(opt.discountBp % 100 ? 1 : 0);
  const rp = (opt.rewardBp / 100).toFixed(opt.rewardBp % 100 ? 1 : 0);
  note.textContent = t('dist.selectedDistributor', { name: opt.label, disc: dp, rwd: rp });
}

function renderDistributorSelect(selectId: 'mDistIn' | 'vDistIn', noteId: 'mDistNote' | 'vDistNote'): void {
  const select = $(selectId) as HTMLSelectElement;
  const prev = select.value;
  select.innerHTML = '';

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = distributorSelectOptions.length > 0 ? t('dist.selectPlaceholder') : t('dist.noDistributors');
  select.appendChild(placeholder);

  for (const opt of distributorSelectOptions) {
    const option = document.createElement('option');
    option.value = opt.address;
    option.textContent = opt.active ? opt.label : `${opt.label} (${t('dener.inactive')})`;
    select.appendChild(option);
  }
  select.value = distributorSelectOptions.some((o) => o.address === prev) ? prev : '';
  select.disabled = distributorSelectOptions.length === 0;
  setDistributorSelectNote(selectId, noteId);
}

function renderDistributorSelects(): void {
  renderDistributorSelect('mDistIn', 'mDistNote');
  renderDistributorSelect('vDistIn', 'vDistNote');
  mResolvedDist = (($('mDistIn') as HTMLSelectElement).value || null);
  vResolvedDist = (($('vDistIn') as HTMLSelectElement).value || null);
}

async function loadDistributorSelectOptions(force = false): Promise<void> {
  if (!force && distributorSelectPromise) return distributorSelectPromise;
  distributorSelectPromise = (async () => {
    $('mDistNote').className = 'm-distnote show';
    $('vDistNote').className = 'm-distnote show';
    $('mDistNote').textContent = t('dist.loadingDistributors');
    $('vDistNote').textContent = t('dist.loadingDistributors');
    try {
      const rows = await sdk.admin.listDistributors();
      distributorSelectOptions = await Promise.all(
        rows.map(async (row) => {
          const name = await adminPrimaryDomain(row.address);
          return {
            address: row.address,
            label: name ? `${name} (${shortAddr(row.address, 6)})` : shortAddr(row.address),
            active: row.active,
            discountBp: row.discountBp,
            rewardBp: row.rewardBp,
          };
        }),
      );
    } catch {
      distributorSelectOptions = [];
      renderDistributorSelects();
      $('mDistNote').className = 'm-distnote show no';
      $('vDistNote').className = 'm-distnote show no';
      $('mDistNote').textContent = t('dist.listError');
      $('vDistNote').textContent = t('dist.listError');
      updateCtaState();
      return;
    }
    renderDistributorSelects();
    updateCtaState();
  })();
  await distributorSelectPromise;
  distributorSelectPromise = null;
}

function onDistributorSelect(selectId: 'mDistIn' | 'vDistIn', noteId: 'mDistNote' | 'vDistNote'): void {
  const select = $(selectId) as HTMLSelectElement;
  if (selectId === 'mDistIn') {
    mResolvedDist = select.value || null;
    setDistributorSelectNote(selectId, noteId);
    void mQuote();
  } else {
    vResolvedDist = select.value || null;
    setDistributorSelectNote(selectId, noteId);
    onAmount();
  }
  updateCtaState();
}

function setAdminRegEnabled(enabled: boolean): void {
  ($('aRegBtn') as HTMLButtonElement).disabled = !enabled;
}

function onAdminRegInput(): void {
  const input = $('aRegAddr') as HTMLInputElement;
  const note = $('aRegNote');
  const raw = input.value.trim();
  const name = raw.toLowerCase();
  window.clearTimeout(distTimers.aRegAddr);
  aResolvedRegName = null;
  aResolvedRegAddr = null;
  setAdminRegEnabled(false);

  if (!name) {
    note.className = 'm-distnote show req';
    note.textContent = t('admin.regRequired');
    return;
  }

  if (isAddress(raw)) {
    aResolvedRegName = name;
    aResolvedRegAddr = raw;
    note.className = 'm-distnote show ok';
    note.textContent = t('admin.regAddressOk', { addr: raw });
    setAdminRegEnabled(true);
    return;
  }

  if (!name.endsWith('.dex')) {
    note.className = 'm-distnote show no';
    note.textContent = t('admin.regInvalid');
    return;
  }

  note.className = 'm-distnote show';
  note.textContent = t('dist.resolving');
  distTimers.aRegAddr = window.setTimeout(async () => {
    const current = input.value.trim().toLowerCase();
    const r = await resolveDexName(name);
    if (current !== name) return;

    if (!r.ok) {
      note.className = 'm-distnote show no';
      note.textContent =
        r.reason === 'no_bnb'
          ? t('dist.noBnbAddr')
          : r.reason === 'error'
            ? t('dist.resolveError')
            : t('dist.nameNotFound');
      return;
    }
    if (!isAddress(r.address)) {
      note.className = 'm-distnote show no';
      note.textContent = t('dist.noBnbAddr');
      return;
    }

    aResolvedRegName = name;
    aResolvedRegAddr = r.address;
    note.className = 'm-distnote show ok';
    note.textContent = t('admin.regResolved', { name, addr: r.address });
    setAdminRegEnabled(true);
  }, 300);
}

// 할인 도메인 필수 → 유효 .dex 조회 전에는 CTA 버튼 비활성화
// (연결 전에는 "지갑 연결" 동작이므로 항상 활성)
function updateCtaState(): void {
  ($('actBtn') as HTMLButtonElement).disabled = connected && !vResolvedDist;
  ($('mActBtn') as HTMLButtonElement).disabled = connected && !mResolvedDist;
}

async function mQuote(): Promise<void> {
  const out = $('mMolOut');
  const drow = $('mDiscountRow');
  if (!hasAddress(PV_GATE_ADDRESS) || !priceLoaded || curBnbWei <= 0n) {
    out.textContent = '—';
    drow.className = 'm-discount';
    return;
  }
  try {
    const distAddr = mResolvedDist;
    const q = await sdk.swap.quote(curBnbWei, distAddr);
    // 표시 기준은 슬라이더가 고른 정확한 수량(curMolWhole). 역산 1wei 오차 방지.
    const baseDisplay = curMolWhole * ONE;
    const discDisplay =
      q.discountBp > 0 ? (baseDisplay * BigInt(10000 + q.discountBp)) / 10000n : baseDisplay;
    out.textContent = fmtMolInt(discDisplay) + ' MOL';
    if (q.discountBp > 0) {
      drow.className = 'm-discount show';
      const extra = discDisplay - baseDisplay;
      const rwStr = Number(formatEther(q.reward)).toLocaleString('en-US', { maximumFractionDigits: 4 });
      drow.innerHTML = t('modal.discountChip', {
        pct: (q.discountBp / 100).toFixed(q.discountBp % 100 ? 1 : 0),
        extra: fmtMolInt(extra),
      });
      $('mRateNote').innerHTML =
        t('modal.rateBase', { base: fmtMolInt(baseDisplay), extra: fmtMolInt(extra) }) +
        (q.reward > 0n ? t('modal.rewardLine', { rwd: rwStr }) : '');
    } else if (q.reward > 0n) {
      drow.className = 'm-discount';
      const rwStr = Number(formatEther(q.reward)).toLocaleString('en-US', { maximumFractionDigits: 4 });
      $('mRateNote').textContent = t('modal.noDiscountReward', { rwd: rwStr });
    } else {
      drow.className = 'm-discount';
      $('mRateNote').textContent = t('modal.distHint');
    }
  } catch {
    out.textContent = '—';
  }
}

async function mAct(): Promise<void> {
  if (!connected) {
    await connectWallet();
    if (connected) {
      const btn = $('mActBtn');
      btn.classList.remove('connect');
      btn.textContent = t('cta.mine');
      await loadPrice();
      await mRefresh();
      refreshDomainBoxes();
      updateCtaState();
    }
    return;
  }
  await mDoSwap();
}

async function mDoSwap(): Promise<void> {
  mClearErr();
  if (!hasAddress(PV_GATE_ADDRESS)) {
    mShowErr(t('modal.noAddrErr'));
    return;
  }
  if (curBnbWei <= 0n) {
    mShowErr(t('modal.needMolAmount'));
    return;
  }
  // 할인 도메인 필수: .dex 가 유효하게 조회되지 않으면 진행 불가
  if (!mResolvedDist) {
    mShowErr(t('dist.required'));
    return;
  }
  const distAddr = mResolvedDist;
  // 슬리피지 보호: 현재 MOL 수량의 99%를 최소 수령으로
  const minMolOut = (curMolWhole * ONE * 99n) / 100n;
  const btn = $('mActBtn') as HTMLButtonElement;
  btn.disabled = true;
  try {
    await sdk.swap.swap(curBnbWei, distAddr, minMolOut, (u: TxUpdate) => {
      if (u.state === TxState.Signing) {
        mSetTx('info', `<span class="sp"></span><div>${t('tx.signing')}</div>`);
      } else if (u.state === TxState.Pending) {
        mSetTx(
          'info',
          `<span class="sp"></span><div>${t('tx.pendingMining')}<br><a href="${CHAIN.explorer}/tx/${u.hash}" target="_blank" rel="noopener">${u.hash?.slice(0, 18)}…</a></div>`,
        );
      } else if (u.state === TxState.Confirming) {
        mSetTx('info', `<span class="sp"></span><div>${t('tx.confirming')}</div>`);
      } else if (u.state === TxState.Success) {
        const got = u.molOut != null ? fmtMolInt(u.molOut) + ' MOL' : t('tx.successDefault');
        mSetTx(
          'ok',
          `<div>${t('tx.successMined', { got })}<br><a href="${CHAIN.explorer}/tx/${u.hash}" target="_blank" rel="noopener">${t('tx.viewTx')}</a><button type="button" id="mAddMolBtn" style="${ADD_MOL_BTN_STYLE}">${tAddMol('tx.addMol', '지갑에 MOL 추가')}</button></div>`,
        );
        void promptAddMol(false);
        document.getElementById('mAddMolBtn')?.addEventListener('click', () => void promptAddMol(true));
      }
    });
    await mRefresh();
    await refresh();
  } catch (err) {
    const code = err instanceof SdkError ? err.code : 'UNKNOWN';
    mSetTx('err', `<div>${t('swap.failPrefix', { msg: humanErr(code, (err as Error).message) })}</div>`);
  } finally {
    btn.disabled = false;
  }
}

function mSetTx(kind: 'info' | 'ok' | 'err', html: string): void {
  const el = $('mTxbox');
  el.className = 'm-txbox show ' + kind;
  el.innerHTML = html;
}
function mShowErr(msg: string): void {
  const el = $('mErrbar');
  el.textContent = msg;
  el.classList.add('show');
}
function mClearErr(): void {
  $('mErrbar').classList.remove('show');
}

// =============================================================
//  지갑 드롭다운 + 역할 (owner / distributor / user)
// =============================================================

let myRole: WalletRole = 'user';

function toggleWalletMenu(): void {
  if (!connected) {
    void connectWallet();
    return;
  }
  $('wmenu').classList.toggle('open');
}

async function detectRole(): Promise<void> {
  if (!hasAddress(PV_GATE_ADDRESS)) return;
  const snap = sdk.wallet.snapshot();
  $('wmAddr').textContent = snap.short ?? '0x…';
  // 내 MOL
  try {
    const info = await sdk.swap.info();
    if (info.myMol !== null) $('wmMolVal').textContent = fmtMolInt(info.myMol) + ' MOL';
  } catch { /* noop */ }

  try {
    myRole = await sdk.admin.role();
  } catch {
    myRole = 'user';
  }
  const roleEl = $('wmRole');
  const denerBtn = $('wmDener');
  const adminBtn = $('wmAdmin');
  denerBtn.style.display = 'none';
  adminBtn.style.display = 'none';
  roleEl.className = 'wmenu-role';
  roleEl.textContent = '';
  if (myRole === 'owner') {
    roleEl.classList.add('owner');
    roleEl.textContent = t('wallet.roleOwner');
    adminBtn.style.display = 'flex';
  } else if (myRole === 'distributor') {
    roleEl.classList.add('distributor');
    roleEl.textContent = t('wallet.roleDist');
    denerBtn.style.display = 'flex';
  }
}

async function copyAddr(): Promise<void> {
  const addr = sdk.wallet.getAddress();
  if (!addr) return;
  try {
    await navigator.clipboard.writeText(addr);
    $('wmCopy').textContent = t('wallet.copied');
    setTimeout(() => ($('wmCopy').textContent = t('wallet.copy')), 1500);
  } catch { /* noop */ }
}

async function copyAdminEmail(): Promise<void> {
  const email = $('dlEmail').textContent?.trim() ?? '';
  if (!email) return;
  const btn = $('dlCopyBtn');
  try {
    await navigator.clipboard.writeText(email);
    btn.textContent = t('wallet.copied');
    btn.classList.add('copied');
    setTimeout(() => {
      btn.textContent = t('wallet.copy');
      btn.classList.remove('copied');
    }, 1500);
  } catch { /* noop */ }
}

async function disconnectWallet(): Promise<void> {
  await sdk.wallet.disconnect();
  reflectDisconnected();
}

// =============================================================
//  총판 대시보드 오버레이
// =============================================================

const DENER_FROM_BLOCK = 0; // 운영 시 배포 블록으로 좁히면 빠름

async function openDenerDashboard(): Promise<void> {
  $('wmenu').classList.remove('open');
  $('denerOverlay').classList.add('open');
  $('denerOverlay').setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  await loadDenerInfo();
}
function closeDener(): void {
  $('denerOverlay').classList.remove('open');
  $('denerOverlay').setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

async function loadDenerInfo(): Promise<void> {
  const info = await sdk.admin.myDistributorInfo();
  if (!info) {
    $('dDisc').textContent = '—';
    return;
  }
  $('dDisc').textContent = (info.discountBp / 100).toFixed(info.discountBp % 100 ? 1 : 0) + '%';
  $('dRwd').textContent = (info.rewardBp / 100).toFixed(info.rewardBp % 100 ? 1 : 0) + '%';
  $('dActive').textContent = info.active ? t('dener.active') : t('dener.inactive');
  ($('dActive') as HTMLElement).style.color = info.active ? 'var(--ok)' : 'var(--err)';

  // owner 잠금이면 설정 숨김
  if (info.ownerSet) {
    ($('dSetBox') as HTMLElement).style.display = 'none';
    ($('dLocked') as HTMLElement).style.display = 'block';
  } else {
    ($('dSetBox') as HTMLElement).style.display = 'block';
    ($('dLocked') as HTMLElement).style.display = 'none';
    const range = $('dDiscRange') as HTMLInputElement;
    range.value = String(Math.round(info.discountBp / 100));
    onDenerDiscChange();
  }

  // 구매자 리스트 (온체인 이벤트)
  void loadDenerBuyers();
}

function onDenerDiscChange(): void {
  const pct = Number(($('dDiscRange') as HTMLInputElement).value);
  $('dDiscVal').textContent = pct + '%';
  const reward = Math.max(0, 5 - pct);
  $('dAutoHint').textContent = t('dener.autoHint', { n: reward });
}

async function applyDenerDiscount(): Promise<void> {
  const pct = Number(($('dDiscRange') as HTMLInputElement).value);
  const bp = pct * 100;
  const btn = $('dApplyBtn') as HTMLButtonElement;
  btn.disabled = true;
  denerTx('info', `<span class="sp"></span><div>${t('dener.signConfirm')}</div>`);
  try {
    const hash = await sdk.admin.setMyDiscount(bp);
    denerTx('ok', `<div>${t('dener.applied', { pct })}<br><a href="${CHAIN.explorer}/tx/${hash}" target="_blank" rel="noopener">${t('common.txLink')}</a></div>`);
    await loadDenerInfo();
  } catch (err) {
    denerTx('err', `<div>${humanTxErr(err)}</div>`);
  } finally {
    btn.disabled = false;
  }
}

async function loadDenerBuyers(): Promise<void> {
  const me = sdk.wallet.getAddress();
  if (!me) return;
  const list = $('dBuyersList');
  list.innerHTML = `<div class="d-empty">${t('common.loading')}</div>`;
  try {
    const buyers = await sdk.admin.buyersOf(me, DENER_FROM_BLOCK);
    $('dBuyerCount').textContent = t('dener.buyerCount', {
      n: new Set(buyers.map((b) => b.buyer.toLowerCase())).size,
    });
    if (buyers.length === 0) {
      list.innerHTML = `<div class="d-empty">${t('dener.noBuyers')}</div>`;
      return;
    }
    list.innerHTML = buyers
      .slice()
      .reverse()
      .map(
        (b) =>
          `<div class="d-buyer"><span class="bw">${b.buyer.slice(0, 8)}…${b.buyer.slice(-4)}</span><span class="bm">${fmtMolInt(b.molOut)} MOL</span></div>`,
      )
      .join('');
  } catch {
    list.innerHTML = `<div class="d-empty">${t('dener.buyersFail')}</div>`;
  }
}

function denerTx(kind: 'info' | 'ok' | 'err', html: string): void {
  const el = $('dTxbox');
  el.className = 'm-txbox show ' + kind;
  el.innerHTML = html;
}

// =============================================================
//  관리자 오버레이 (마스터-디테일)
// =============================================================

let adminRows: DistributorRow[] = [];
let selectedDist: string | null = null;
const adminPrimaryDomains = new Map<string, string | null>();

function shortAddr(addr: string, head = 10): string {
  return `${addr.slice(0, head)}…${addr.slice(-4)}`;
}

async function adminPrimaryDomain(addr: string): Promise<string | null> {
  const key = addr.toLowerCase();
  if (adminPrimaryDomains.has(key)) return adminPrimaryDomains.get(key) ?? null;
  const name = await resolvePrimaryDomain(addr);
  adminPrimaryDomains.set(key, name);
  return name;
}

function adminAddrLabel(addr: string, name: string | null, head = 10): string {
  return name ? `${shortAddr(addr, head)} (${name})` : shortAddr(addr, head);
}

async function hydrateAdminPrimaryDomains(): Promise<void> {
  await Promise.all(
    adminRows.map(async (row) => {
      const name = await adminPrimaryDomain(row.address);
      const item = $('aList').querySelector<HTMLElement>(`.a-litem[data-addr="${row.address}"] .la`);
      if (item) item.textContent = adminAddrLabel(row.address, name);
      if (selectedDist === row.address) $('aDAddr').textContent = name ? `${row.address} (${name})` : row.address;
    }),
  );
}

async function openAdmin(): Promise<void> {
  $('wmenu').classList.remove('open');
  $('adminOverlay').classList.add('open');
  $('adminOverlay').setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  await loadAdminList();
}
function closeAdmin(): void {
  $('adminOverlay').classList.remove('open');
  $('adminOverlay').setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

async function loadAdminList(): Promise<void> {
  const list = $('aList');
  list.innerHTML = `<div class="d-empty">${t('common.loading')}</div>`;
  try {
    adminRows = await sdk.admin.listDistributors();
    if (adminRows.length === 0) {
      list.innerHTML = `<div class="d-empty">${t('admin.noDistributors')}</div>`;
      return;
    }
    list.innerHTML = adminRows
      .map(
        (r) =>
          `<div class="a-litem${r.address === selectedDist ? ' sel' : ''}" data-addr="${r.address}">
            <div class="la">${shortAddr(r.address)}</div>
            <div class="lr">${t('admin.listRates', {
              disc: r.discountBp / 100,
              rwd: r.rewardBp / 100,
              cls: r.active ? 'on' : 'off',
              status: r.active ? t('dener.active') : t('dener.inactive'),
            })}</div>
          </div>`,
      )
      .join('');
    list.querySelectorAll<HTMLElement>('.a-litem').forEach((el) => {
      el.addEventListener('click', () => void selectDist(el.dataset.addr!));
    });
    void hydrateAdminPrimaryDomains();
  } catch {
    list.innerHTML = `<div class="d-empty">${t('common.queryFail')}</div>`;
  }
}

async function selectDist(addr: string): Promise<void> {
  selectedDist = addr;
  $('aList').querySelectorAll<HTMLElement>('.a-litem').forEach((el) => {
    el.classList.toggle('sel', el.dataset.addr === addr);
  });
  const row = adminRows.find((r) => r.address === addr);
  if (!row) return;
  ($('aDetailEmpty') as HTMLElement).style.display = 'none';
  ($('aDetailBody') as HTMLElement).style.display = 'block';
  $('aDAddr').textContent = addr;
  void adminPrimaryDomain(addr).then((name) => {
    if (selectedDist === addr) $('aDAddr').textContent = name ? `${addr} (${name})` : addr;
  });

  // 현재 할인/보상 표시
  $('aDDiscNow').textContent = row.discountBp / 100 + '%';
  $('aDRwdNow').textContent = row.rewardBp / 100 + '%';

  // 활성 토글 (현재 상태 반영)
  ($('aActiveChk') as HTMLInputElement).checked = row.active;
  $('aActiveDesc').textContent = row.active ? t('admin.activeDescOn') : t('admin.activeDescOff');

  // 잠금 토글 (ownerSet = 관리자 잠금)
  ($('aLockChk') as HTMLInputElement).checked = row.ownerSet;
  $('aLockDesc').textContent = row.ownerSet ? t('admin.lockDescOn') : t('admin.lockDescOff');

  const discR = $('aDiscRange') as HTMLInputElement;
  const rwdR = $('aRwdRange') as HTMLInputElement;
  discR.value = String(Math.round(row.discountBp / 100));
  rwdR.value = String(Math.round(row.rewardBp / 100));
  $('aDiscLabel').textContent = row.discountBp / 100 + '%';
  $('aRwdLabel').textContent = row.rewardBp / 100 + '%';
  $('aDBuyers').textContent = '…';
  try {
    const cnt = await sdk.admin.buyerCountOf(addr, DENER_FROM_BLOCK);
    $('aDBuyers').textContent = t('dener.buyerCount', { n: cnt });
  } catch {
    $('aDBuyers').textContent = '?';
  }
}

async function registerDist(): Promise<void> {
  const input = $('aRegAddr') as HTMLInputElement;
  const note = $('aRegNote');
  const raw = input.value.trim();
  const name = raw.toLowerCase();
  if (!name) {
    setAdminRegEnabled(false);
    note.className = 'm-distnote show req';
    note.textContent = t('admin.regRequired');
    return;
  }

  let addr: string | null = null;
  if (isAddress(raw)) {
    addr = raw;
    aResolvedRegName = name;
    aResolvedRegAddr = addr;
  } else if (!name.endsWith('.dex')) {
    setAdminRegEnabled(false);
    note.className = 'm-distnote show no';
    note.textContent = t('admin.regInvalid');
    return;
  }

  if (!addr) {
    addr = aResolvedRegName === name ? aResolvedRegAddr : null;
  }
  if (!addr) {
    setAdminRegEnabled(false);
    window.clearTimeout(distTimers.aRegAddr);
    note.className = 'm-distnote show';
    note.textContent = t('dist.resolving');
    const r = await resolveDexName(name);
    if (!r.ok) {
      note.className = 'm-distnote show no';
      note.textContent =
        r.reason === 'no_bnb'
          ? t('dist.noBnbAddr')
          : r.reason === 'error'
            ? t('dist.resolveError')
            : t('dist.nameNotFound');
      return;
    }
    addr = r.address;
    if (!isAddress(addr)) {
      note.className = 'm-distnote show no';
      note.textContent = t('dist.noBnbAddr');
      return;
    }
    aResolvedRegName = name;
    aResolvedRegAddr = addr;
  }
  setAdminRegEnabled(false);
  note.className = 'm-distnote show ok';
  note.textContent = isAddress(raw) ? t('admin.regAddressOk', { addr }) : t('admin.regResolved', { name, addr });

  adminTx('info', `<span class="sp"></span><div>${t('admin.regSign')}</div>`);
  try {
    const hash = await sdk.admin.register(addr);
    adminTx('ok', `<div>${t('admin.registered')}<br><a href="${CHAIN.explorer}/tx/${hash}" target="_blank" rel="noopener">${t('common.txLink')}</a></div>`);
    input.value = '';
    aResolvedRegName = null;
    aResolvedRegAddr = null;
    setAdminRegEnabled(false);
    note.className = 'm-distnote';
    note.textContent = '';
    await loadAdminList();
    void loadDistributorSelectOptions(true);
  } catch (err) {
    adminTx('err', `<div>${humanTxErr(err)}</div>`);
    setAdminRegEnabled(aResolvedRegName === input.value.trim().toLowerCase() && !!aResolvedRegAddr);
  }
}

async function applyAdminRates(): Promise<void> {
  if (!selectedDist) return;
  const disc = Number(($('aDiscRange') as HTMLInputElement).value) * 100;
  const rwd = Number(($('aRwdRange') as HTMLInputElement).value) * 100;
  adminTx('info', `<span class="sp"></span><div>${t('admin.setSign')}</div>`);
  try {
    const hash = await sdk.admin.setRates(selectedDist, disc, rwd);
    adminTx('ok', `<div>${t('admin.ratesSet')}<br><a href="${CHAIN.explorer}/tx/${hash}" target="_blank" rel="noopener">${t('common.txLink')}</a></div>`);
    await loadAdminList();
    void loadDistributorSelectOptions(true);
    await selectDist(selectedDist);
  } catch (err) {
    adminTx('err', `<div>${humanTxErr(err)}</div>`);
  }
}

async function onActiveToggle(): Promise<void> {
  if (!selectedDist) return;
  const chk = $('aActiveChk') as HTMLInputElement;
  const want = chk.checked;
  chk.disabled = true;
  adminTx('info', `<span class="sp"></span><div>${t('admin.toggleSign')}</div>`);
  try {
    const hash = await sdk.admin.setActive(selectedDist, want);
    adminTx('ok', `<div>${t('admin.toggleResult', { state: want ? t('admin.activated') : t('admin.deactivated') })}<br><a href="${CHAIN.explorer}/tx/${hash}" target="_blank" rel="noopener">${t('common.txLink')}</a></div>`);
    await loadAdminList();
    void loadDistributorSelectOptions(true);
    await selectDist(selectedDist);
  } catch (err) {
    chk.checked = !want; // 실패 시 원상복구
    adminTx('err', `<div>${humanTxErr(err)}</div>`);
  } finally {
    chk.disabled = false;
  }
}

async function onLockToggle(): Promise<void> {
  if (!selectedDist) return;
  const chk = $('aLockChk') as HTMLInputElement;
  const wantLock = chk.checked;
  chk.disabled = true;
  adminTx('info', `<span class="sp"></span><div>${t('admin.toggleSign')}</div>`);
  try {
    if (wantLock) {
      // 잠금 = 현재 슬라이더 값으로 ownerSetRates 호출
      const disc = Number(($('aDiscRange') as HTMLInputElement).value) * 100;
      const rwd = Number(($('aRwdRange') as HTMLInputElement).value) * 100;
      const hash = await sdk.admin.setRates(selectedDist, disc, rwd);
      adminTx('ok', `<div>${t('admin.lockApplied', { disc: disc / 100, rwd: rwd / 100 })}<br><a href="${CHAIN.explorer}/tx/${hash}" target="_blank" rel="noopener">${t('common.txLink')}</a></div>`);
    } else {
      // 해제 = 총판 자율로
      const hash = await sdk.admin.release(selectedDist);
      adminTx('ok', `<div>${t('admin.released')}<br><a href="${CHAIN.explorer}/tx/${hash}" target="_blank" rel="noopener">${t('common.txLink')}</a></div>`);
    }
    await loadAdminList();
    void loadDistributorSelectOptions(true);
    await selectDist(selectedDist);
  } catch (err) {
    chk.checked = !wantLock;
    adminTx('err', `<div>${humanTxErr(err)}</div>`);
  } finally {
    chk.disabled = false;
  }
}

async function removeDist(): Promise<void> {
  if (!selectedDist) return;
  adminTx('info', `<span class="sp"></span><div>${t('admin.removeSign')}</div>`);
  try {
    const hash = await sdk.admin.remove(selectedDist);
    adminTx('ok', `<div>${t('admin.removed')}<br><a href="${CHAIN.explorer}/tx/${hash}" target="_blank" rel="noopener">${t('common.txLink')}</a></div>`);
    selectedDist = null;
    ($('aDetailBody') as HTMLElement).style.display = 'none';
    ($('aDetailEmpty') as HTMLElement).style.display = 'flex';
    await loadAdminList();
    void loadDistributorSelectOptions(true);
  } catch (err) {
    adminTx('err', `<div>${humanTxErr(err)}</div>`);
  }
}

function adminTx(kind: 'info' | 'ok' | 'err', html: string): void {
  const el = $('aTxbox');
  el.className = 'm-txbox show ' + kind;
  el.innerHTML = html;
}

// =============================================================
//  참여 마퀴 띠 — 수호자 합류 스트림 (온체인 Swapped 이벤트)
// =============================================================

// 현재 로케일 기준 합류 문구 (언어 전환 시 재렌더에서 자동 반영)
function denerMessages(): string[] {
  return [t('feed.msg1'), t('feed.msg2'), t('feed.msg3'), t('feed.msg4'), t('feed.msg5')];
}

let feedItems: FeedItem[] = [];
let feedUnsub: (() => void) | null = null;

function pickMsg(seed: string): string {
  // 주소 기반 결정적 선택(같은 사람은 같은 문구)
  const msgs = denerMessages();
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return msgs[h % msgs.length];
}

function feedItemHtml(it: FeedItem, fresh = false): string {
  const msg = pickMsg(it.buyer);
  return `<span class="dt-item${fresh ? ' fresh' : ''}">
    <span class="dt-msg">⛏ ${msg}</span>
    <span class="dt-addr">${it.short}</span>
    <span class="dt-dot">·</span>
    <span class="dt-mol">${it.molWhole} MOL</span>
  </span>`;
}

function renderTicker(): void {
  const track = $('dtTrack');
  const ticker = $('denerTicker');
  if (feedItems.length === 0) {
    ticker.classList.remove('show');
    return;
  }
  // 자연스러운 무한 스크롤: 항목들을 2벌 이어붙임 (translateX -50% 매칭)
  const once = feedItems.map((it) => feedItemHtml(it)).join('');
  track.innerHTML = once + once;
  ticker.classList.add('show');
}

async function initDenerTicker(): Promise<void> {
  try {
    feedItems = await sdk.feed.recent(20);
  } catch {
    feedItems = [];
  }
  renderTicker();

  // 실시간 신규 구독
  feedUnsub?.();
  feedUnsub = sdk.feed.subscribe((it) => {
    // 맨 앞에 추가, 최대 30건 유지
    feedItems = [it, ...feedItems.filter((x) => x.txHash !== it.txHash)].slice(0, 30);
    // fresh 강조로 새 항목만 다시 렌더
    const track = $('dtTrack');
    const ticker = $('denerTicker');
    const once = feedItems.map((x, i) => feedItemHtml(x, i === 0)).join('');
    track.innerHTML = once + once;
    ticker.classList.add('show');
  });
}

// =============================================================
//  언어(i18n)
// =============================================================

// 언어 전환 시 JS가 그린 동적 영역을 다시 칠한다.
// (정적 DOM은 setLocale 내부 applyDom 이 자동 처리)
function repaintDynamic(): void {
  ($('langSelect') as HTMLSelectElement).value = getLocale();

  // 연결 상태에 따라 라벨이 달라지는 버튼들
  if (connected) {
    const snap = sdk.wallet.snapshot();
    $('wlabel').textContent = snap.short ?? t('wallet.connected');
    $('actBtn').textContent = t('cta.mine');
    $('mActBtn').textContent = t('cta.mine');
  } else {
    $('wlabel').textContent = t('wallet.connect');
    $('actBtn').textContent = t('wallet.connect');
    $('mActBtn').textContent = t('wallet.connect');
  }

  // 티커 문구 재렌더 (같은 주소 → 같은 문구 유지)
  renderTicker();

  // 대표 도메인 안내(패널/모달)도 현재 언어로 다시 렌더
  refreshDomainBoxes();
  renderDistributorSelects();

  // 체인 데이터 기반 표시(잔액·진척·역할)는 재조회로 갱신
  if (connected && hasAddress(PV_GATE_ADDRESS)) {
    void refresh();
    void detectRole();
    if ($('modalOverlay').classList.contains('open')) void mRefresh();
  }
}

function wire(): void {
  $('wbtn').addEventListener('click', () => void toggleWalletMenu());
  $('actBtn').addEventListener('click', () => void onAct());
  ($('bnbIn') as HTMLInputElement).addEventListener('input', onAmount);
  ($('vDistIn') as HTMLSelectElement).addEventListener('change', () => onDistributorSelect('vDistIn', 'vDistNote'));
  void loadDistributorSelectOptions();
  document.querySelectorAll<HTMLElement>('[data-amt]').forEach((b) => {
    b.addEventListener('click', () => {
      const v = b.dataset.amt!;
      if (v === 'max') void setMax();
      else {
        ($('bnbIn') as HTMLInputElement).value = v;
        onAmount();
      }
    });
  });

  // ── 서임 모달 ──
  $('becomeBtn').addEventListener('click', () => void onBecome());
  $('modalClose').addEventListener('click', closeModal);
  $('modalOverlay').addEventListener('click', (e) => {
    if (e.target === $('modalOverlay')) closeModal();
  });
  $('mActBtn').addEventListener('click', () => void mAct());
  ($('mMolRange') as HTMLInputElement).addEventListener('input', onMolRange);
  ($('mDistIn') as HTMLSelectElement).addEventListener('change', () => onDistributorSelect('mDistIn', 'mDistNote'));

  // ── 지갑 드롭다운 ──
  $('wmCopy').addEventListener('click', () => void copyAddr());
  $('wmDisconnect').addEventListener('click', () => void disconnectWallet());
  $('wmDener').addEventListener('click', () => void openDenerDashboard());
  $('wmAdmin').addEventListener('click', () => void openAdmin());
  // 바깥 클릭 시 드롭다운 닫기
  document.addEventListener('click', (e) => {
    const wrap = document.querySelector('.wallet-wrap');
    if (wrap && !wrap.contains(e.target as Node)) $('wmenu').classList.remove('open');
  });

  // ── 총판 대시보드 오버레이 ──
  $('denerClose').addEventListener('click', closeDener);
  $('denerOverlay').addEventListener('click', (e) => {
    if (e.target === $('denerOverlay')) closeDener();
  });
  ($('dDiscRange') as HTMLInputElement).addEventListener('input', onDenerDiscChange);
  $('dApplyBtn').addEventListener('click', () => void applyDenerDiscount());
  $('dlCopyBtn').addEventListener('click', () => void copyAdminEmail());

  // ── 관리자 오버레이 ──
  $('adminClose').addEventListener('click', closeAdmin);
  $('adminOverlay').addEventListener('click', (e) => {
    if (e.target === $('adminOverlay')) closeAdmin();
  });
  $('aRegBtn').addEventListener('click', () => void registerDist());
  ($('aRegAddr') as HTMLInputElement).addEventListener('input', onAdminRegInput);
  ($('aRegAddr') as HTMLInputElement).addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !($('aRegBtn') as HTMLButtonElement).disabled) void registerDist();
  });
  ($('aDiscRange') as HTMLInputElement).addEventListener('input', () => {
    $('aDiscLabel').textContent = ($('aDiscRange') as HTMLInputElement).value + '%';
  });
  ($('aRwdRange') as HTMLInputElement).addEventListener('input', () => {
    $('aRwdLabel').textContent = ($('aRwdRange') as HTMLInputElement).value + '%';
  });
  $('aApplyBtn').addEventListener('click', () => void applyAdminRates());
  ($('aActiveChk') as HTMLInputElement).addEventListener('change', () => void onActiveToggle());
  ($('aLockChk') as HTMLInputElement).addEventListener('change', () => void onLockToggle());
  $('aRemoveBtn').addEventListener('click', () => void removeDist());

  // ── 언어 드롭다운 ──
  const langSel = $('langSelect') as HTMLSelectElement;
  langSel.value = getLocale();
  langSel.addEventListener('change', () => setLocale(langSel.value as Locale));
  onLocaleChange(repaintDynamic);

  // ── ESC: 열린 오버레이 닫기 ──
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if ($('modalOverlay').classList.contains('open')) closeModal();
    else if ($('denerOverlay').classList.contains('open')) closeDener();
    else if ($('adminOverlay').classList.contains('open')) closeAdmin();
  });

  // 지갑이 외부에서 끊기면 UI 동기화
  sdk.wallet.onChange((snap) => {
    if (!snap.connected && connected) reflectDisconnected();
  });

  // 지갑 stuck 알림 → 사용자에게 새로고침 안내(모바일 MetaMask 응답 없음 대응)
  sdk.wallet.onStuck?.(() => {
    showStuckNotice();
  });

  initScroll();
  void initDenerTicker(); // 참여 마퀴 띠 (지갑 연결과 무관하게 즉시)
  void restoreSession(); // 새로고침 후 기존 세션 복원
}

initI18n(); // 저장된 로케일로 정적 DOM 도색 (sdk 불필요 → 즉시)

// 부팅: 환경에 맞는 어댑터로 SDK 생성 후 UI 배선.
//  · window.ethereum 있음(MetaMask 내장 브라우저/확장) → injected (동기 경로)
//  · 없음 + REOWN_PROJECT_ID → Reown 동적 로드 (일반 모바일 QR/딥링크)
async function boot(): Promise<void> {
  try {
    sdk = await DigMolSDK.create();
  } catch (err) {
    console.error('[boot] SDK 생성 실패, injected 로 폴백:', err);
    sdk = new DigMolSDK(); // 생성자 기본 = injected
  }
  wire();
}

void boot();